import { Calendar, Clock, MapPin, Share2, Edit3, Trash2, Badge } from 'lucide-react';
import { useState } from 'react';
import { Button } from '../../../components/ui/button';
import { ShareModal } from './ShareModal';
import { DeleteEventModal } from './DeleteEventModal';
import { CATEGORIES } from '../../../constants/categories';
import { MODALITIES } from '../../../constants/modalities';
import { AddToCalendarModal } from './AddToCalendarModal';

interface Host {
  id: string;
  name: string;
  email: string;
  avatar?: string; // Nuevo campo para el avatar
}

interface EventData {
  id: string;
  title: string;
  description: string;
  startDate: string;
  endDate: string;
  startTime: string;
  endTime: string;
  location: string;
  organizer: Host;
  coHosts: Host[];
  imageUrl?: string;
  type: 'in-person' | 'online' | 'hybrid'; // Tipo de evento
  category?: string; // Categoría del evento
  modality?: string; // Modalidad del evento
}

interface EventCardProps {
  eventData: EventData;
  onDelete?: () => void;
  onEdit?: () => void;
  // Agregar props para controlar qué botones mostrar
  canEdit?: boolean;
  canDelete?: boolean;
}

export const EventCard = ({ 
  eventData,
  onDelete,
  onEdit,
  canEdit = false,
  canDelete = false
}: EventCardProps) => {
  
  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isImageVisible, setIsImageVisible] = useState(true);
  const [hiddenAvatarIds, setHiddenAvatarIds] = useState<Set<string>>(new Set());
  
  // Get all hosts (organizer + co-hosts)
  const allHosts = [eventData.organizer, ...eventData.coHosts];

  const handleHostClick = (host: Host) => {
    if (host?.email) {
      window.open(`mailto:${host.email}`, '_blank');
      return;
    }
    
  };

  const handleShareClick = () => {
    setIsShareModalOpen(true);
  };

  const handleDeleteClick = () => {
    setIsDeleteModalOpen(true);
  };

  const handleEditClick = () => {
    onEdit?.();
  };

  const handleDeleteConfirm = async () => {
    setIsDeleting(true);
    try {
      await onDelete?.();
      setIsDeleteModalOpen(false);
    } catch (error) {
      console.error('Error deleting event:', error);
    } finally {
      setIsDeleting(false);
    }
  };

  const showImage = Boolean(eventData.imageUrl) && isImageVisible;

  return (
    <div className="border bg-white rounded-lg p-4 flex flex-col gap-4">
      {/* Grid container para el layout principal */}
      <div className="grid grid-cols-1 lg:grid-cols-[1fr_auto] gap-4 lg:gap-6">
        {/* Contenido principal (imagen + texto) */}
        <div className={`grid grid-cols-1 ${showImage ? 'sm:grid-cols-[auto_1fr]' : 'sm:grid-cols-1'} gap-4`}>
          {showImage && (
            <img 
              src={eventData.imageUrl as string}
              alt="Event" 
              className="w-full sm:w-54 h-48 sm:h-36 rounded-md object-cover" 
              onError={() => setIsImageVisible(false)}
            />
          )}
          <div className="mt-0 sm:mt-2">
            <div className="flex flex-wrap items-center gap-2">
              {eventData.category && (
                <span style={CATEGORIES.find(cat => cat.name === eventData.category)?.color ? { backgroundColor: CATEGORIES.find(cat => cat.name === eventData.category)?.color, color: CATEGORIES.find(cat => cat.name === eventData.category)?.textColor } : {}} className="text-xs px-2 py-1 rounded-md font-medium">
                  {eventData.category}
                </span>
              )}
              {eventData.modality && (
                <span className="text-xs font-medium px-3 py-1 rounded-full" style={{ backgroundColor: MODALITIES.find(mod => mod.value === eventData.modality)?.color, color: MODALITIES.find(mod => mod.value === eventData.modality)?.textColor }}>
                  {MODALITIES.find(mod => mod.value === eventData.modality)?.name}
                </span>
              )}
            </div>
            <h2 className="text-xl mt-4 font-bold" style={{fontSize:'1.5rem'}}>{eventData.title}</h2>
            <p className="text-lg mt-1 text-muted-foreground" style={{fontSize:'1rem'}}>{eventData.description}</p>
          </div>
        </div>
        
        {/* Botones de acción */}
        <div className="grid grid-cols-3 lg:grid-cols-1 gap-2 lg:gap-2 lg:auto-rows-min">
          {/* Siempre mostrar el botón de compartir */}
          <Button 
            className="lg:w-full flex items-center justify-center gap-2" 
            variant="outline" 
            style={{borderColor: '#0095FF', color: '#0095FF'}}
            onClick={handleShareClick}
          >
            <Share2 className="w-4 h-4" /> 
            <span className="hidden sm:inline">Share</span>
          </Button>
          
          {/* Solo mostrar botón de editar si el usuario puede editar */}
          {canEdit && onEdit && (
            <Button 
              className="lg:w-full flex items-center justify-center gap-2" 
              variant="outline" 
              style={{borderColor: '#6D29F6', color: '#6D29F6'}}
              onClick={handleEditClick}
            >
              <Edit3 className="w-4 h-4" /> 
              <span className="hidden sm:inline">Edit</span>
            </Button>
          )}
          
          {/* Solo mostrar botón de eliminar si el usuario puede eliminar */}
          {canDelete && onDelete && (
            <Button 
              className="lg:w-full flex items-center justify-center gap-2" 
              variant="outline" 
              style={{borderColor: '#FF5C5F', color: '#FF5C5F'}}
              onClick={handleDeleteClick}
            >
              <Trash2 className="w-4 h-4" /> 
              <span className="hidden sm:inline">Delete</span>
            </Button>
          )}
        </div>
      </div>
      
      <div className="flex items-center gap-3 text-sm text-muted-foreground mt-1">
        <div className="flex items-center -space-x-2">
          {allHosts.map((host, index) => {
            const showImg = Boolean(host.avatar) && !hiddenAvatarIds.has(host.id);
            return (
              <div
                key={host.id}
                className="w-6 h-6 bg-purple-100 rounded-full border-2 border-white cursor-pointer hover:scale-110 transition-transform flex items-center justify-center overflow-hidden"
                style={{
                  zIndex: allHosts.length - index
                }}
                title={host.name}
                onClick={() => handleHostClick(host)}
              >
                {showImg ? (
                  <img 
                    src={host.avatar as string} 
                    alt={host.name}
                    className="w-full h-full object-cover"
                    onError={() => setHiddenAvatarIds(prev => { const next = new Set(prev); next.add(host.id); return next; })}
                  />
                ) : (
                  <span className="text-xs font-medium text-purple-600">
                    {host.name.charAt(0)}
                  </span>
                )}
              </div>
            );
          })}
        </div>
        <span>Hosted by</span>
        
        <div className="flex items-center gap-1">
          {allHosts.map((host, index) => (
            <span key={host.id}>
              <span 
                style={{color: '#6D29F6'}}
                className="font-medium cursor-pointer hover:underline"
                onClick={() => handleHostClick(host)}
              >
                {host.name}
              </span>
              {index < allHosts.length - 1 && <span className="text-muted-foreground"> and </span>}
            </span>
          ))}
        </div>
      </div>
      
      <div className="flex gap-4 text-sm text-muted-foreground">
        <div className="flex items-center gap-1">
          <Calendar className="w-4 h-4" /> {eventData.startDate} - {eventData.endDate}
        </div>
        <div className="flex items-center gap-1">
          <Clock className="w-4 h-4" /> {eventData.startTime} - {eventData.endTime}
        </div>
        <div className="flex items-center gap-1">
          <span className="font-medium">PTM Week</span>
        </div>
      </div>

      <ShareModal
        isOpen={isShareModalOpen}
        onClose={() => setIsShareModalOpen(false)}
        title={eventData.title}
        description={eventData.description}
      />

      <DeleteEventModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={handleDeleteConfirm}
        eventTitle={eventData.title}
        isLoading={isDeleting}
      />
    </div>
  );
};
